import React, { useState, useMemo, Fragment, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, FileText, Search, Filter, RefreshCw } from 'lucide-react';

/**
 * Modal de sélection personnalisée pour l'export PDF
 *
 * Permet de choisir précisément ce qui sera inclus dans l'export PDF :
 * - Sections : Statistiques, User Stories, Jalons
 * - Catégories d'US : Backlog, En Test, Tests à valider, Done
 * - User Stories individuelles
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - État d'ouverture du modal
 * @param {Function} props.onClose - Fonction pour fermer le modal
 * @param {Function} props.onExport - Callback avec les sélections pour générer le PDF
 * @param {Array} props.userStories - Liste des user stories
 * @param {Array} props.epics - Liste des epics
 * @param {Array} props.milestones - Liste des jalons
 * @param {string} props.clientName - Nom du client
 * @param {string} props.projectName - Nom du projet
 * @param {boolean} props.isGenerating - État de génération du PDF
 */
const ExportPDFModal = ({
  isOpen,
  onClose,
  onExport,
  userStories = [],
  epics = [],
  milestones = [],
  clientName,
  projectName,
  isGenerating = false
}) => {
  // État des sélections
  const [selections, setSelections] = useState({
    includeSections: {
      statistics: true,
      userStories: true,
      milestones: true
    },
    categories: {
      'Backlog': true,
      'En Test': true,
      'Tests à valider': true,
      'Done': true
    },
    selectedUserStories: new Set()
  });

  // État pour la recherche et le filtre de la section 3
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

  /**
   * Initialisation : tout sélectionné par défaut
   */
  useEffect(() => {
    if (isOpen) {
      const allUSIds = new Set(userStories.map(us => us.id));
      setSelections({
        includeSections: {
          statistics: true,
          userStories: true,
          milestones: true
        },
        categories: {
          'Backlog': true,
          'En Test': true,
          'Tests à valider': true,
          'Done': true
        },
        selectedUserStories: allUSIds
      });
      setSearchTerm('');
      setSelectedCategoryFilter('all');
    }
  }, [isOpen, userStories]);

  /**
   * Toggle d'une section principale
   */
  const handleSectionToggle = (sectionKey) => {
    setSelections(prev => ({
      ...prev,
      includeSections: {
        ...prev.includeSections,
        [sectionKey]: !prev.includeSections[sectionKey]
      }
    }));
  };

  /**
   * Toggle d'une catégorie
   * Si décochée, désélectionner toutes les US de cette catégorie
   */
  const handleCategoryToggle = (categoryKey) => {
    setSelections(prev => {
      const newCategoryState = !prev.categories[categoryKey];
      const newCategories = { ...prev.categories, [categoryKey]: newCategoryState };

      // Si on décoche, désélectionner toutes les US de cette catégorie
      if (!newCategoryState) {
        const categoryUSIds = userStories
          .filter(us => us.category === categoryKey)
          .map(us => us.id);
        const newSelectedUS = new Set(prev.selectedUserStories);
        categoryUSIds.forEach(id => newSelectedUS.delete(id));

        return {
          ...prev,
          categories: newCategories,
          selectedUserStories: newSelectedUS
        };
      }

      // Si on coche, sélectionner toutes les US de cette catégorie
      const categoryUSIds = userStories
        .filter(us => us.category === categoryKey)
        .map(us => us.id);
      const newSelectedUS = new Set(prev.selectedUserStories);
      categoryUSIds.forEach(id => newSelectedUS.add(id));

      return {
        ...prev,
        categories: newCategories,
        selectedUserStories: newSelectedUS
      };
    });
  };

  /**
   * Toggle d'une US individuelle
   */
  const handleUSToggle = (usId) => {
    setSelections(prev => {
      const newSelectedUS = new Set(prev.selectedUserStories);
      if (newSelectedUS.has(usId)) {
        newSelectedUS.delete(usId);
      } else {
        newSelectedUS.add(usId);
      }
      return { ...prev, selectedUserStories: newSelectedUS };
    });
  };

  /**
   * Tout sélectionner / Tout désélectionner
   */
  const handleSelectAll = () => {
    const allUSIds = new Set(filteredUserStories.map(us => us.id));
    setSelections(prev => ({
      ...prev,
      selectedUserStories: allUSIds
    }));
  };

  const handleDeselectAll = () => {
    setSelections(prev => ({
      ...prev,
      selectedUserStories: new Set()
    }));
  };

  /**
   * Calculer les statistiques par catégorie
   */
  const getCategoryStats = useCallback((categoryKey) => {
    const stories = userStories.filter(us => us.category === categoryKey);
    return {
      count: stories.length,
      storyPoints: stories.reduce((sum, us) => sum + (us.storyPoints || 0), 0),
      hours: stories.reduce((sum, us) => sum + (us.hourlyRate || 0), 0)
    };
  }, [userStories]);

  const categoryStats = useMemo(() => ({
    'Backlog': getCategoryStats('Backlog'),
    'En Test': getCategoryStats('En Test'),
    'Tests à valider': getCategoryStats('Tests à valider'),
    'Done': getCategoryStats('Done')
  }), [getCategoryStats]);

  /**
   * Filtrer les US pour la section 3 (recherche + filtre catégorie)
   */
  const filteredUserStories = useMemo(() => {
    let filtered = userStories;

    // Filtre par catégorie
    if (selectedCategoryFilter !== 'all') {
      filtered = filtered.filter(us => us.category === selectedCategoryFilter);
    }

    // Filtre par recherche
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(us =>
        us.name.toLowerCase().includes(search) ||
        (us.code && us.code.toLowerCase().includes(search))
      );
    }

    return filtered;
  }, [userStories, selectedCategoryFilter, searchTerm]);

  /**
   * Validation avant export
   */
  const validateSelections = (selections) => {
    const hasAnySection = Object.values(selections.includeSections).some(v => v);
    if (!hasAnySection) {
      return { valid: false, message: 'Sélectionnez au moins une section.' };
    }

    if (selections.includeSections.userStories) {
      const hasAnyCategory = Object.values(selections.categories).some(v => v);
      if (!hasAnyCategory) {
        return { valid: false, message: 'Sélectionnez au moins une catégorie d\'User Stories.' };
      }
      if (selections.selectedUserStories.size === 0) {
        return { valid: false, message: 'Sélectionnez au moins une User Story.' };
      }
    }

    return { valid: true };
  };

  /**
   * Générer le PDF avec les sélections
   */
  const handleGenerate = () => {
    const validation = validateSelections(selections);
    if (!validation.valid) {
      alert(validation.message);
      return;
    }
    onExport(selections);
  };

  /**
   * Calculer le résumé pour le footer
   */
  const summary = useMemo(() => {
    const selectedSections = Object.entries(selections.includeSections)
      .filter(([_, selected]) => selected)
      .length;
    const selectedUSCount = selections.selectedUserStories.size;
    const selectedStoryPoints = userStories
      .filter(us => selections.selectedUserStories.has(us.id))
      .reduce((sum, us) => sum + (us.storyPoints || 0), 0);

    return {
      sections: selectedSections,
      userStories: selectedUSCount,
      storyPoints: selectedStoryPoints
    };
  }, [selections, userStories]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
      >
        {/* Overlay avec fade */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 sticky top-0 z-10 bg-white rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <Dialog.Title className="text-2xl font-bold text-gray-900">
                          Personnaliser l'export PDF
                        </Dialog.Title>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {projectName} - {clientName}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content scrollable */}
                <div className="px-6 py-6 max-h-[calc(80vh-200px)] overflow-y-auto space-y-6">

                  {/* SECTION 1 : Sections à inclure */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      1️⃣ Sections à inclure
                    </h3>
                    <div className="space-y-3">
                      <CheckboxSection
                        label="Statistiques Globales"
                        checked={selections.includeSections.statistics}
                        onChange={() => handleSectionToggle('statistics')}
                        subInfo="Vue d'ensemble du projet avec métriques clés"
                      />
                      <CheckboxSection
                        label="User Stories"
                        checked={selections.includeSections.userStories}
                        onChange={() => handleSectionToggle('userStories')}
                        subInfo="Liste détaillée des user stories par catégorie"
                        count={userStories.length}
                      />
                      <CheckboxSection
                        label="Jalons du Projet"
                        checked={selections.includeSections.milestones}
                        onChange={() => handleSectionToggle('milestones')}
                        subInfo="Timeline et statut des jalons"
                        count={milestones.length}
                      />
                    </div>
                  </div>

                  {/* SECTION 2 : Catégories d'User Stories */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      2️⃣ Catégories d'User Stories
                    </h3>
                    <div className="space-y-3">
                      {['Backlog', 'En Test', 'Tests à valider', 'Done'].map(categoryKey => {
                        const stats = categoryStats[categoryKey];
                        const isDisabled = !selections.includeSections.userStories;

                        return (
                          <CheckboxSection
                            key={categoryKey}
                            label={categoryKey}
                            checked={selections.categories[categoryKey]}
                            onChange={() => handleCategoryToggle(categoryKey)}
                            disabled={isDisabled}
                            subInfo={`${stats.count} US • ${stats.storyPoints} SP • ${stats.hours}h`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* SECTION 3 : Sélection individuelle */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      3️⃣ Sélection individuelle
                    </h3>

                    {/* Barre de recherche et filtre */}
                    <div className="mb-4 flex gap-3">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Rechercher une User Story..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          disabled={!selections.includeSections.userStories}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="relative w-48">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        <select
                          value={selectedCategoryFilter}
                          onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                          disabled={!selections.includeSections.userStories}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm appearance-none bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="all">Toutes les catégories</option>
                          <option value="Backlog">Backlog</option>
                          <option value="En Test">En Test</option>
                          <option value="Tests à valider">Tests à valider</option>
                          <option value="Done">Done</option>
                        </select>
                      </div>
                    </div>

                    {/* Boutons Tout sélectionner / Tout désélectionner */}
                    <div className="mb-3 flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {filteredUserStories.length} User Stor{filteredUserStories.length > 1 ? 'ies' : 'y'} affichée{filteredUserStories.length > 1 ? 's' : ''}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSelectAll}
                          disabled={!selections.includeSections.userStories || filteredUserStories.length === 0}
                          className="text-sm text-purple-600 hover:text-purple-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Tout sélectionner
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={handleDeselectAll}
                          disabled={!selections.includeSections.userStories || filteredUserStories.length === 0}
                          className="text-sm text-purple-600 hover:text-purple-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Tout désélectionner
                        </button>
                      </div>
                    </div>

                    {/* Liste des US avec checkboxes */}
                    <div className={`max-h-64 overflow-y-auto border border-gray-200 rounded-lg ${!selections.includeSections.userStories ? 'bg-gray-50' : ''}`}>
                      {filteredUserStories.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 text-sm">
                          {searchTerm || selectedCategoryFilter !== 'all'
                            ? 'Aucune User Story trouvée avec ces filtres'
                            : 'Aucune User Story disponible'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {filteredUserStories.map(us => {
                            const isSelected = selections.selectedUserStories.has(us.id);
                            const isDisabled = !selections.includeSections.userStories;

                            return (
                              <label
                                key={us.id}
                                className={`flex items-center gap-3 px-4 py-3 ${
                                  isDisabled
                                    ? 'cursor-not-allowed opacity-50'
                                    : 'cursor-pointer hover:bg-purple-50'
                                } transition-colors`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleUSToggle(us.id)}
                                  disabled={isDisabled}
                                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                      {us.name}
                                    </span>
                                    <span className="text-xs text-gray-500 flex-shrink-0">
                                      {us.code || us.id}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                      {us.category}
                                    </span>
                                    {us.storyPoints > 0 && (
                                      <span className="text-xs text-purple-600 font-medium">
                                        {us.storyPoints} SP
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-2xl">
                  <div className="flex items-center justify-between">
                    {/* Résumé */}
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-purple-600">
                        {summary.userStories} US
                      </span>
                      {summary.storyPoints > 0 && (
                        <>
                          {' • '}
                          <span className="font-semibold text-purple-600">
                            {summary.storyPoints} SP
                          </span>
                        </>
                      )}
                      {' • '}
                      <span className="font-semibold text-purple-600">
                        {summary.sections} section{summary.sections > 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Boutons */}
                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        disabled={isGenerating}
                        className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Génération...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4" />
                            Générer PDF
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * Composant réutilisable pour les checkboxes de sections et catégories
 */
const CheckboxSection = ({ label, checked, onChange, disabled, subInfo, count }) => (
  <label
    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
      disabled
        ? 'opacity-50 cursor-not-allowed bg-gray-50'
        : 'hover:bg-purple-50'
    } ${
      checked ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
    }`}
  >
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
      />
      <div>
        <span className="font-medium text-gray-900">{label}</span>
        {subInfo && (
          <span className="text-sm text-gray-500 ml-2">{subInfo}</span>
        )}
      </div>
    </div>
    {count !== undefined && (
      <span className="text-sm font-semibold text-gray-600 bg-white px-2.5 py-1 rounded-md border border-gray-200">
        {count}
      </span>
    )}
  </label>
);

export default ExportPDFModal;
